import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Avatar } from '@rallyshift/design-system'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { useOrganization } from '../contexts/OrganizationContext'
import LanguageSwitcher from './LanguageSwitcher'
import {
  Users,
  Calendar,
  CalendarDays,
  UserCheck,
  LogOut,
  Menu,
  X,
  UserCog,
  User,
  BookOpen,
  CheckSquare,
  Settings,
  Sparkles,
  MessageSquare,
} from 'lucide-react'
import { useUnreadMessages } from '../hooks/useUnreadMessages'

interface LayoutProps {
  children: React.ReactNode
}

interface NavEntry {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { profile, signOut, isVolunteer, isAdmin } = useAuth()
  const { organization } = useOrganization()
  const { t } = useLocale()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const canMessage = isVolunteer || isAdmin
  const unread = useUnreadMessages(canMessage)

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const navigation: NavEntry[] = [
    { name: t('navigation.dashboard'), href: '/dashboard', icon: UserCheck },
    ...(isVolunteer
      ? [
          { name: t('navigation.shifts'), href: '/shifts', icon: Calendar },
          { name: t('navigation.myShifts'), href: '/my-shifts', icon: UserCheck },
          ...(organization?.volunteer_check_in_enabled === false
            ? []
            : [{ name: 'Inchecken', href: '/check-in', icon: Sparkles }]),
        ]
      : []),
    ...(canMessage
      ? [{ name: t('navigation.messages'), href: '/messages', icon: MessageSquare, badge: unread }]
      : []),
  ]

  const adminNavigation: NavEntry[] = isAdmin
    ? [
        { name: t('navigation.volunteers'), href: '/volunteers', icon: Users },
        { name: t('navigation.volunteerApproval'), href: '/volunteer-approval', icon: UserCog },
        { name: 'Evenementen', href: '/events', icon: CalendarDays },
        { name: 'Nieuwe dienst', href: '/shifts/new', icon: Calendar },
        { name: 'Evenementdag', href: '/event-day', icon: CheckSquare },
        { name: 'Organisatie', href: '/organisation', icon: Settings },
        { name: 'Taken', href: '/admin-todo', icon: CheckSquare },
        { name: 'Wat is er nieuw', href: '/system-updates', icon: BookOpen },
      ]
    : []

  const navLink = (item: NavEntry, onNavigate?: () => void) => {
    const isActive =
      location.pathname === item.href ||
      (item.href === '/messages' && location.pathname.startsWith('/messages/'))
    return (
      <Link
        key={item.href}
        to={item.href}
        onClick={onNavigate}
        aria-current={isActive ? 'page' : undefined}
        className={`group flex min-h-touch-min items-center rounded-md px-3 text-sm font-semibold transition-colors duration-fast ${
          isActive
            ? 'bg-tenant-soft text-tenant-ink'
            : 'text-text-secondary hover:bg-surface-sunken hover:text-text-primary'
        }`}
      >
        <item.icon className="mr-3 h-5 w-5" />
        <span className="flex-1">{item.name}</span>
        {!!item.badge && (
          <span
            className="min-w-[20px] rounded-pill bg-tenant-brand px-1.5 text-center text-xs font-bold leading-5 text-tenant-on-brand"
            aria-label={`${item.badge} unread`}
          >
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </Link>
    )
  }

  /* The tenant's mark, with RallyShift's endorsement underneath rather than beside it,
     so a volunteer sees their own organisation first. */
  const brand = (
    <Link to="/dashboard" className="flex items-center gap-3 overflow-hidden">
      {organization?.logo_url ? (
        <img
          src={organization.logo_url}
          alt={organization.name}
          className="h-10 w-auto max-w-[150px] object-contain"
        />
      ) : (
        <span className="truncate font-display text-display-sm font-bold text-text-primary">
          {organization?.name ?? 'RallyShift'}
        </span>
      )}
    </Link>
  )

  const sidebarBody = (onNavigate?: () => void) => (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navigation.map((item) => navLink(item, onNavigate))}

        {adminNavigation.length > 0 && (
          <>
            <div className="px-3 pb-1 pt-5 text-xs font-bold uppercase tracking-[0.06em] text-text-muted">
              Beheer
            </div>
            {adminNavigation.map((item) => navLink(item, onNavigate))}
          </>
        )}

        {navLink({ name: t('navigation.profile'), href: '/profile', icon: User }, onNavigate)}
      </nav>

      <div className="border-t border-border p-4">
        <Link
          to="/profile"
          onClick={onNavigate}
          className="-m-2 flex items-center rounded-md p-2 transition-colors hover:bg-surface-sunken"
        >
          <Avatar
            name={
              [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
              profile?.email ||
              ''
            }
            src={profile?.avatar_url}
            size={36}
          />
          <div className="ml-3 min-w-0">
            <p className="truncate text-sm font-semibold text-text-primary">
              {profile?.first_name && profile?.last_name
                ? `${profile.first_name} ${profile.last_name}`
                : profile?.email}
            </p>
            <p className="text-xs capitalize text-text-muted">{profile?.role}</p>
          </div>
        </Link>

        <div className="mt-3 space-y-2">
          <div className="px-1">
            <LanguageSwitcher />
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex min-h-touch-min w-full items-center rounded-md px-3 text-sm font-semibold text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary"
          >
            <LogOut className="mr-3 h-4 w-4" />
            {t('auth.signOut')}
          </button>
        </div>

        <p className="mt-4 px-1 text-xs text-text-muted">
          mogelijk gemaakt door <span className="font-bold text-clay-600">RallyShift</span>
        </p>
      </div>
    </>
  )

  return (
    <div className="flex h-screen bg-surface-page">
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div
          className="fixed inset-0 bg-surface-inverse/50"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-surface-card shadow-overlay">
          <div className="flex h-16 items-center justify-between border-b border-border px-4">
            {brand}
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label="Sluit menu"
              className="min-h-touch-min min-w-touch-min text-text-muted hover:text-text-primary"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {sidebarBody(() => setSidebarOpen(false))}
        </div>
      </div>

      <div className="hidden lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col border-r border-border bg-surface-card">
          <div className="flex h-16 items-center px-4">{brand}</div>
          {sidebarBody()}
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex h-16 items-center justify-between border-b border-border bg-surface-card px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            className="min-h-touch-min min-w-touch-min text-text-secondary hover:text-text-primary"
          >
            <Menu className="h-6 w-6" />
          </button>
          {brand}
          <div className="w-11" />
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  )
}

export default Layout
